import React, { useState, useEffect } from 'react';
import { trainingGuideAPI } from '../utils/api';

const AdminTrainingGuides = ({ onEditGuide, onCreateNew }) => {
    const [guides, setGuides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, published, draft

    useEffect(() => {
        fetchGuides();
    }, []);

    const fetchGuides = async () => {
        try {
            const response = await trainingGuideAPI.getAllAdmin();
            setGuides(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching guides:', error);
            setGuides([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this guide?')) return;

        try {
            await trainingGuideAPI.delete(id);
            setGuides(guides.filter(g => g._id !== id));
            alert('Guide deleted successfully');
        } catch (error) {
            console.error('Error deleting guide:', error);
            alert('Failed to delete guide');
        }
    };

    const handleTogglePublish = async (guide) => {
        try {
            const response = await trainingGuideAPI.update(guide._id, {
                published: !guide.published
            });
            setGuides(guides.map(g => g._id === guide._id ? response.data : g));
        } catch (error) {
            console.error('Error updating guide:', error);
        }
    };

    const filteredGuides = guides.filter(guide => {
        if (filter === 'published') return guide.published;
        if (filter === 'draft') return !guide.published;
        return true;
    });

    if (loading) {
        return <div className="text-center py-12 font-light text-gray-600">Loading guides...</div>;
    }

    return (
        <div className="bg-white border border-gray-200 p-8 h-full flex flex-col">
            <div className="flex justify-between items-center mb-8 flex-shrink-0">
                <h2 className="text-3xl font-light tracking-tight">Training Guides</h2>
                <button
                    onClick={onCreateNew}
                    className="bg-black text-white px-6 py-3 hover:bg-gray-900 transition-all duration-300 font-light tracking-wide"
                >
                    + New Guide
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-8 mb-8 border-b border-gray-200 flex-shrink-0">
                <button
                    onClick={() => setFilter('all')}
                    className={`py-3 px-2 font-light text-sm border-b-2 transition-all ${
                        filter === 'all'
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-600 hover:text-black'
                    }`}
                >
                    All ({guides.length})
                </button>
                <button
                    onClick={() => setFilter('published')}
                    className={`py-3 px-2 font-light text-sm border-b-2 transition-all ${
                        filter === 'published'
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-600 hover:text-black'
                    }`}
                >
                    Published ({guides.filter(g => g.published).length})
                </button>
                <button
                    onClick={() => setFilter('draft')}
                    className={`py-3 px-2 font-light text-sm border-b-2 transition-all ${
                        filter === 'draft'
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-600 hover:text-black'
                    }`}
                >
                    Drafts ({guides.filter(g => !g.published).length})
                </button>
            </div>

            {filteredGuides.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-600 font-light mb-4">No guides found</p>
                    <button
                        onClick={onCreateNew}
                        className="text-black border-b-2 border-gray-300 hover:border-black pb-1 font-light transition-all"
                    >
                        Create your first guide →
                    </button>
                </div>
            ) : (
                <div className="flex-grow overflow-y-auto">
                    <div className="space-y-4">
                        {filteredGuides.map(guide => (
                            <div key={guide._id} className="border border-gray-200 p-6 hover:border-black hover:shadow-md transition-all duration-300">
                                <div className="flex gap-6">
                                    {guide.thumbnail && (
                                        <img
                                            src={guide.thumbnail}
                                            alt={guide.title}
                                            className="w-32 h-32 object-cover rounded"
                                        />
                                    )}
                                    <div className="flex-grow">
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h3 className="text-lg font-light text-black tracking-tight">{guide.title}</h3>
                                                <p className="text-sm text-gray-600 font-light mt-1">{guide.description}</p>
                                            </div>
                                            <span className={`text-xs font-light px-3 py-1 rounded ${
                                                guide.published
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {guide.published ? 'Published' : 'Draft'}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-4 text-sm text-gray-600 font-light mb-4">
                                            <span>👁️ {guide.views} views</span>
                                            <span>📅 {new Date(guide.createdAt).toLocaleDateString()}</span>
                                        </div>

                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => onEditGuide(guide)}
                                                className="px-4 py-2 border border-black text-black hover:bg-black hover:text-white transition-all duration-300 font-light text-sm"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleTogglePublish(guide)}
                                                className={`px-4 py-2 text-sm font-light transition-all duration-300 ${
                                                    guide.published
                                                        ? 'border border-gray-400 text-gray-700 hover:border-black'
                                                        : 'bg-green-600 text-white hover:bg-green-700'
                                                }`}
                                            >
                                                {guide.published ? 'Unpublish' : 'Publish'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(guide._id)}
                                                className="px-4 py-2 border border-red-400 text-red-600 hover:bg-red-50 transition-all duration-300 font-light text-sm"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTrainingGuides;
